import React, { useState } from 'react';
import { Card, Button } from '@/components/ui';
import { FormItem, Form } from '@/components/ui/Form';
import { triggerSuccessMessage, triggerErrorMessage } from '@/components/ui/toast/notifications';
import { useManageAccount } from '@/api/account';
import { isCurrentPasswordError } from '@/api/account';
import PasswordInput from '@/components/shared/PasswordInput';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ZodType } from 'zod';

type ChangePasswordFormSchema = {
  current_password: string;
  new_password: string;
};

const validationSchema: ZodType<ChangePasswordFormSchema> = z
  .object({
    current_password: z
      .string({ required_error: 'Please enter your current password' })
      .min(1, { message: 'Please enter your current password' }),
    new_password: z
      .string({ required_error: 'Please enter your new password' })
      .min(7, { message: 'New password must be at least 7 characters long' }),
  })
  .refine((data) => data.current_password !== data.new_password, {
    message: 'New password must be different from current password',
    path: ['new_password'],
  });

const ChangePassword: React.FC = () => {
  const [isSubmitting, setSubmitting] = useState(false);
  const { updatePassword } = useManageAccount();

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setError,
  } = useForm<ChangePasswordFormSchema>({
    defaultValues: {
      current_password: '',
      new_password: '',
    },
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (values: ChangePasswordFormSchema) => {
    setSubmitting(true);

    try {
      await updatePassword(values);

      // Clear form on success
      reset();

      // Show success notification
      triggerSuccessMessage('Password changed successfully');
    } catch (error: any) {
      // Check if it's a wrong current password error
      if (isCurrentPasswordError(error)) {
        setError('current_password', {
          type: 'manual',
          message: 'The provided password does not match your current password',
        });
      } else if (error?.response?.data?.errors) {
        // Handle validation errors from API
        const apiErrors = error.response.data.errors;

        if (apiErrors.current_password) {
          setError('current_password', {
            type: 'manual',
            message: apiErrors.current_password[0],
          });
        }

        if (apiErrors.new_password) {
          setError('new_password', {
            type: 'manual',
            message: apiErrors.new_password[0],
          });
        }
      } else {
        // Show error notification for other errors
        triggerErrorMessage(error?.message || 'Failed to change password');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      header={{
        content: <h2 className="text-lg font-semibold">Change Password</h2>,
        bordered: true,
      }}
    >
      <Form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormItem
          label="Current Password"
          invalid={Boolean(errors.current_password)}
          errorMessage={errors.current_password?.message}
        >
          <Controller
            name="current_password"
            control={control}
            render={({ field }) => (
              <PasswordInput
                placeholder="Enter your current password"
                autoComplete="current-password"
                {...field}
              />
            )}
          />
        </FormItem>

        <FormItem
          label="New Password"
          invalid={Boolean(errors.new_password)}
          errorMessage={errors.new_password?.message}
        >
          <Controller
            name="new_password"
            control={control}
            render={({ field }) => (
              <PasswordInput
                placeholder="Enter your new password"
                autoComplete="new-password"
                {...field}
              />
            )}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Must be at least 7 characters long
          </p>
        </FormItem>

        <div className="flex justify-end pt-2">
          <Button
            variant="solid"
            type="submit"
            loading={isSubmitting}
          >
            {isSubmitting ? 'Changing...' : 'Change Password'}
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default ChangePassword;

